import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Clock, Save, Check, ChevronLeft, ChevronRight, FileText, Download, Eye, List, Trash2, Edit2, X } from 'lucide-react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const QuickPunchPage = () => {
  const [searchParams] = useSearchParams();
  const punchKey = searchParams.get('key') || '';
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  
  // Main tab: 'punch' or 'invoices'
  const [activeTab, setActiveTab] = useState('punch');
  
  // Invoices list
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  
  // Clients for invoice creation
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  
  // Invoice editing
  const [editingInvoice, setEditingInvoice] = useState(null);
  
  const [todayData, setTodayData] = useState(null);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [pricing, setPricing] = useState({ currency: 'GBP' });
  
  // Week view
  const [showWeek, setShowWeek] = useState(false);
  const [weekData, setWeekData] = useState(null);
  const [currentWeekStart, setCurrentWeekStart] = useState('');
  
  // Editing specific day
  const [editingDay, setEditingDay] = useState(null); // { date, day_of_week, start_time, end_time }

  useEffect(() => {
    if (punchKey) {
      fetchToday();
      initWeek();
      fetchClients();
    } else {
      setError('Missing key parameter');
      setLoading(false);
    }
  }, [punchKey]);

  // Fetch invoices on initial load AND when tab changes
  useEffect(() => {
    if (punchKey) {
      fetchInvoices();
    }
  }, [punchKey]);

  // Refresh when switching to invoices tab
  useEffect(() => {
    if (activeTab === 'invoices' && punchKey) {
      fetchInvoices();
    }
  }, [activeTab]);

  const fetchClients = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/punch/clients?key=${punchKey}`);
      setClients(data);
      if (data.length > 0 && !selectedClientId) {
        setSelectedClientId(data[0].id);
      }
    } catch (err) {
      console.log('Failed to load clients');
    }
  };

  const fetchInvoices = async () => {
    setLoadingInvoices(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/punch/invoices?key=${punchKey}`);
      setInvoices(data);
    } catch (err) {
      toast.error('Failed to load invoices');
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleViewInvoice = (invoiceId) => {
    const pdfUrl = `${BACKEND_URL}/api/punch/invoice-pdf/${invoiceId}?key=${punchKey}`;
    window.open(pdfUrl, '_blank');
  };

  const handleDownloadInvoice = (invoice) => {
    const pdfUrl = `${BACKEND_URL}/api/punch/invoice-pdf/${invoice.id}?key=${punchKey}`;
    // Create a link and trigger download
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.target = '_blank';
    link.download = `${invoice.invoice_number || 'invoice'}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Deschide și folosește Print (Ctrl+P) → Save as PDF');
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Ștergi această factură?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/punch/invoices/${invoiceId}?key=${punchKey}`);
      toast.success('Factură ștearsă!');
      fetchInvoices();
    } catch (err) {
      toast.error('Eroare la ștergere');
    }
  };

  const handleEditInvoice = (invoice) => {
    setEditingInvoice({ ...invoice });
  };

  const handleSaveInvoice = async () => {
    if (!editingInvoice) return;
    try {
      await axios.put(
        `${BACKEND_URL}/api/punch/invoices/${editingInvoice.id}?key=${punchKey}`,
        editingInvoice
      );
      toast.success('Factură actualizată!');
      setEditingInvoice(null);
      fetchInvoices();
    } catch (err) {
      toast.error('Eroare la salvare');
    }
  };

  const initWeek = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const monday = new Date(today);
    monday.setDate(today.getDate() - diff);
    setCurrentWeekStart(monday.toISOString().split('T')[0]);
  };

  useEffect(() => {
    if (currentWeekStart && punchKey && showWeek) {
      fetchWeek();
    }
  }, [currentWeekStart, showWeek]);

  const fetchToday = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/punch/today?key=${punchKey}`);
      setTodayData(data);
      setPricing(data.pricing || { currency: 'GBP' });
      if (data.entry) {
        setStartTime(data.entry.start_time || '');
        setEndTime(data.entry.end_time || '');
      }
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const fetchWeek = async () => {
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/punch/week?key=${punchKey}&week_start=${currentWeekStart}`
      );
      setWeekData(data);
    } catch (err) {
      console.error('Failed to fetch week');
    }
  };

  const handleSave = async () => {
    if (!startTime || !endTime) {
      toast.error('Enter both start and end time');
      return;
    }

    setSaving(true);
    setSaved(false);
    
    try {
      const formData = new FormData();
      formData.append('key', punchKey);
      formData.append('date', todayData.date);
      formData.append('day_of_week', todayData.day_of_week);
      formData.append('start_time', startTime);
      formData.append('end_time', endTime);

      const { data } = await axios.post(`${BACKEND_URL}/api/punch/save`, formData);
      
      toast.success(`${data.message}! ${data.hours}h = ${formatCurrency(data.amount)}`);
      setSaved(true);
      fetchToday();
      
      // Reset saved indicator after 3 seconds
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    const symbol = pricing.currency === 'EUR' ? '€' : '£';
    return `${symbol}${(amount || 0).toFixed(2)}`;
  };

  const formatHoursMinutes = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  const handlePrevWeek = () => {
    const date = new Date(currentWeekStart);
    date.setDate(date.getDate() - 7);
    setCurrentWeekStart(date.toISOString().split('T')[0]);
    setEditingDay(null);
  };

  const handleNextWeek = () => {
    const date = new Date(currentWeekStart);
    date.setDate(date.getDate() + 7);
    setCurrentWeekStart(date.toISOString().split('T')[0]);
    setEditingDay(null);
  };

  // Create invoice and download PDF
  const handleCreateInvoice = async () => {
    if (!weekData || weekData.entries.length === 0) {
      toast.error('No entries to invoice');
      return;
    }

    if (!selectedClientId) {
      toast.error('Selectează un client');
      return;
    }

    setCreatingInvoice(true);
    try {
      // Create invoice via API (same format as admin)
      const formData = new FormData();
      formData.append('key', punchKey);
      formData.append('week_start', currentWeekStart);
      formData.append('client_id', selectedClientId);
      
      const { data } = await axios.post(`${BACKEND_URL}/api/punch/create-invoice`, formData);
      
      if (data.invoice_id) {
        // Show appropriate message based on whether invoice is new or existing
        if (data.existing) {
          toast.info(`Factura ${data.invoice_number} există deja!`);
        } else {
          toast.success(`Factura ${data.invoice_number} creată!`);
          // Refresh invoices list
          fetchInvoices();
        }
        
        // Open PDF in new window for printing/saving
        const pdfUrl = `${BACKEND_URL}/api/punch/invoice-pdf/${data.invoice_id}?key=${punchKey}`;
        window.open(pdfUrl, '_blank');
        
        toast.success('Factură deschisă! Folosește Print (Ctrl+P) pentru a salva ca PDF');
      }
    } catch (err) {
      console.error('Invoice error:', err);
      toast.error(err.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setCreatingInvoice(false);
    }
  };

  // Save entry for a specific day (used in week view editing)
  const handleSaveDay = async () => {
    if (!editingDay || !editingDay.start_time || !editingDay.end_time) {
      toast.error('Enter both start and end time');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('key', punchKey);
      formData.append('date', editingDay.date);
      formData.append('day_of_week', editingDay.day_of_week);
      formData.append('start_time', editingDay.start_time);
      formData.append('end_time', editingDay.end_time);

      const { data } = await axios.post(`${BACKEND_URL}/api/punch/save`, formData);
      
      toast.success(`Saved! ${data.hours}h = ${formatCurrency(data.amount)}`);
      setEditingDay(null);
      fetchWeek(); // Refresh week data
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Start editing a specific day
  const handleEditDay = (date, dayOfWeek, existingEntry) => {
    setEditingDay({
      date: date,
      day_of_week: dayOfWeek,
      start_time: existingEntry?.start_time || '',
      end_time: existingEntry?.end_time || ''
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="text-red-400 text-xl mb-4">⚠️ {error}</div>
          <p className="text-gray-400 text-sm">Check your link or contact admin</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-900 text-white">
      <Toaster position="top-center" richColors />
      
      {/* Header - Mai mic */}
      <div className="bg-primary/20 p-3 text-center">
        <h1 className="text-lg font-bold flex items-center justify-center gap-2">
          <Clock size={20} />
          Quick Punch
        </h1>
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b border-white/10">
        <button
          onClick={() => { setActiveTab('punch'); setShowWeek(false); }}
          className={`flex-1 py-2 text-center text-sm font-semibold transition-colors ${
            activeTab === 'punch' 
              ? 'bg-primary/20 text-primary border-b-2 border-primary' 
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Clock size={16} className="inline mr-1" />
          Pontaj
        </button>
        <button
          onClick={() => setActiveTab('invoices')}
          className={`flex-1 py-2 text-center text-sm font-semibold transition-colors ${
            activeTab === 'invoices' 
              ? 'bg-primary/20 text-primary border-b-2 border-primary' 
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <FileText size={16} className="inline mr-1" />
          Facturi ({invoices.length})
        </button>
      </div>

      {/* INVOICES TAB */}
      {activeTab === 'invoices' && (
        <div className="p-3 space-y-3">
          {/* Refresh button at top */}
          <button
            onClick={fetchInvoices}
            disabled={loadingInvoices}
            className="w-full py-2 text-sm text-primary border border-primary/30 rounded-lg flex items-center justify-center gap-2"
          >
            {loadingInvoices ? 'Se încarcă...' : '🔄 Reîncarcă'}
          </button>

          {/* Edit Invoice Modal */}
          {editingInvoice && (
            <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
              <div className="bg-slate-900 rounded-xl p-4 w-full max-w-md max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold">Editare {editingInvoice.invoice_number}</h3>
                  <button onClick={() => setEditingInvoice(null)} className="text-gray-400">
                    <X size={24} />
                  </button>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-gray-400 block mb-1">Client</label>
                    <select
                      value={editingInvoice.client_id || ''}
                      onChange={(e) => {
                        const client = clients.find(c => c.id === e.target.value);
                        setEditingInvoice({
                          ...editingInvoice,
                          client_id: e.target.value,
                          client_name: client?.name || ''
                        });
                      }}
                      className="w-full bg-slate-800 border border-white/20 rounded-lg px-3 py-2"
                    >
                      {clients.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="text-xs text-gray-400 block mb-1">Status</label>
                    <select
                      value={editingInvoice.status || 'draft'}
                      onChange={(e) => setEditingInvoice({...editingInvoice, status: e.target.value})}
                      className="w-full bg-slate-800 border border-white/20 rounded-lg px-3 py-2"
                    >
                      <option value="draft">Draft</option>
                      <option value="sent">Sent</option>
                      <option value="paid">Paid</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="text-xs text-gray-400 block mb-1">Data Factură</label>
                    <input
                      type="date"
                      value={editingInvoice.invoice_date || ''}
                      onChange={(e) => setEditingInvoice({...editingInvoice, invoice_date: e.target.value})}
                      className="w-full bg-slate-800 border border-white/20 rounded-lg px-3 py-2"
                    />
                  </div>
                  
                  <div>
                    <label className="text-xs text-gray-400 block mb-1">Data Scadență</label>
                    <input
                      type="date"
                      value={editingInvoice.due_date || ''}
                      onChange={(e) => setEditingInvoice({...editingInvoice, due_date: e.target.value})}
                      className="w-full bg-slate-800 border border-white/20 rounded-lg px-3 py-2"
                    />
                  </div>
                  
                  <div>
                    <label className="text-xs text-gray-400 block mb-1">Total ({editingInvoice.currency || 'GBP'})</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editingInvoice.total || editingInvoice.total_amount || 0}
                      onChange={(e) => setEditingInvoice({
                        ...editingInvoice, 
                        total: parseFloat(e.target.value),
                        total_amount: parseFloat(e.target.value),
                        subtotal: parseFloat(e.target.value)
                      })}
                      className="w-full bg-slate-800 border border-white/20 rounded-lg px-3 py-2"
                    />
                  </div>
                  
                  <div>
                    <label className="text-xs text-gray-400 block mb-1">Note</label>
                    <textarea
                      value={editingInvoice.notes || ''}
                      onChange={(e) => setEditingInvoice({...editingInvoice, notes: e.target.value})}
                      rows={2}
                      className="w-full bg-slate-800 border border-white/20 rounded-lg px-3 py-2"
                    />
                  </div>
                </div>
                
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => setEditingInvoice(null)}
                    className="flex-1 py-2 border border-white/20 rounded-lg"
                  >
                    Anulează
                  </button>
                  <button
                    onClick={handleSaveInvoice}
                    className="flex-1 py-2 bg-primary text-white rounded-lg font-semibold"
                  >
                    Salvează
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {loadingInvoices ? (
            <div className="text-center py-8 text-gray-400">Se încarcă...</div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <FileText size={40} className="mx-auto mb-3 opacity-50" />
              <p>Nu există facturi.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {invoices.map((invoice) => {
                const currencySymbol = (invoice.currency === 'GBP' || !invoice.currency) ? '£' : '€';
                const total = invoice.total || invoice.total_amount || 0;
                return (
                  <div 
                    key={invoice.id}
                    className="bg-slate-800/50 rounded-lg p-3 border border-white/10"
                  >
                    {/* Row 1: Number, Client, Status */}
                    <div className="flex items-center justify-between mb-1">
                      <div>
                        <span className="font-mono text-primary font-bold">{invoice.invoice_number}</span>
                        <span className="text-gray-400 text-sm ml-2">{invoice.client_name || '-'}</span>
                      </div>
                      <span className={`px-2 py-0.5 text-xs rounded uppercase font-semibold ${
                        invoice.status === 'paid' 
                          ? 'bg-green-500/20 text-green-400'
                          : invoice.status === 'sent'
                          ? 'bg-blue-500/20 text-blue-400'
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {invoice.status || 'draft'}
                      </span>
                    </div>
                    
                    {/* Row 2: Dates */}
                    <div className="text-xs text-gray-500 mb-2">
                      {invoice.invoice_date && (
                        <span>📅 {new Date(invoice.invoice_date).toLocaleDateString('ro-RO')}</span>
                      )}
                      {invoice.due_date && (
                        <span className="ml-3">⏰ {new Date(invoice.due_date).toLocaleDateString('ro-RO')}</span>
                      )}
                    </div>
                    
                    {/* Row 3: Amount and Actions */}
                    <div className="flex items-center justify-between">
                      <div className="text-xl font-bold text-green-400">
                        {currencySymbol}{total.toFixed(2)}
                      </div>
                      
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleViewInvoice(invoice.id)}
                          className="p-2 bg-primary/20 text-primary rounded-lg"
                          title="Vezi PDF"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          onClick={() => handleDownloadInvoice(invoice)}
                          className="p-2 bg-green-500/20 text-green-400 rounded-lg"
                          title="Download PDF"
                        >
                          <Download size={18} />
                        </button>
                        <button
                          onClick={() => handleEditInvoice(invoice)}
                          className="p-2 bg-blue-500/20 text-blue-400 rounded-lg"
                          title="Editează"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteInvoice(invoice.id)}
                          className="p-2 bg-red-500/20 text-red-400 rounded-lg"
                          title="Șterge"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* PUNCH TAB - Today's Entry */}
      {activeTab === 'punch' && !showWeek && (
        <div className="p-4 space-y-6">
          {/* Date Display */}
          <div className="text-center py-4">
            <div className="text-4xl font-bold text-primary">{todayData?.day_of_week}</div>
            <div className="text-gray-400 mt-1">
              {new Date(todayData?.date).toLocaleDateString('en-GB', { 
                day: 'numeric', month: 'long', year: 'numeric' 
              })}
            </div>
          </div>

          {/* Time Inputs */}
          <div className="bg-slate-800/50 rounded-2xl p-6 space-y-6">
            <div>
              <label className="text-sm text-gray-400 uppercase tracking-wider block mb-2">
                Start Time
              </label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                onClick={(e) => e.target.showPicker && e.target.showPicker()}
                className="w-full bg-slate-900 border-2 border-primary/30 rounded-xl px-4 py-4 text-2xl text-center focus:outline-none focus:border-primary appearance-none"
                style={{ colorScheme: 'dark', minHeight: '60px' }}
              />
            </div>

            <div>
              <label className="text-sm text-gray-400 uppercase tracking-wider block mb-2">
                End Time
              </label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                onClick={(e) => e.target.showPicker && e.target.showPicker()}
                className="w-full bg-slate-900 border-2 border-primary/30 rounded-xl px-4 py-4 text-2xl text-center focus:outline-none focus:border-primary appearance-none"
                style={{ colorScheme: 'dark', minHeight: '60px' }}
              />
            </div>

            {/* Quick Time Buttons */}
            <div className="space-y-3">
              <p className="text-xs text-gray-500 text-center">Quick Start Times:</p>
              <div className="flex flex-wrap gap-2 justify-center">
                {['05:00', '05:30', '06:00', '06:30', '07:00'].map(time => (
                  <button
                    key={time}
                    onClick={() => setStartTime(time)}
                    className={`px-3 py-2 rounded-lg text-sm ${startTime === time ? 'bg-primary text-white' : 'bg-slate-700 text-gray-300'}`}
                  >
                    {time}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 text-center mt-3">Quick End Times:</p>
              <div className="flex flex-wrap gap-2 justify-center">
                {['15:00', '15:30', '16:00', '17:00', '18:00'].map(time => (
                  <button
                    key={time}
                    onClick={() => setEndTime(time)}
                    className={`px-3 py-2 rounded-lg text-sm ${endTime === time ? 'bg-primary text-white' : 'bg-slate-700 text-gray-300'}`}
                  >
                    {time}
                  </button>
                ))}
              </div>
            </div>

            {/* Current Rate */}
            <div className="text-center text-sm text-gray-400">
              Rate: {formatCurrency(
                todayData?.day_of_week === 'Saturday' ? pricing.saturday_rate :
                todayData?.day_of_week === 'Sunday' ? pricing.sunday_rate :
                pricing.weekday_rate
              )}/hr
            </div>

            {/* Existing Entry Info */}
            {todayData?.entry && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 text-center">
                <div className="text-green-400 text-sm uppercase tracking-wider mb-1">Today's Total</div>
                <div className="text-2xl font-bold text-green-400">
                  {formatHoursMinutes(todayData.entry.minutes_worked)} = {formatCurrency(todayData.entry.amount)}
                </div>
              </div>
            )}
          </div>

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={saving || !startTime || !endTime}
            className={`w-full py-5 rounded-2xl text-xl font-bold flex items-center justify-center gap-3 transition-all ${
              saved 
                ? 'bg-green-500 text-white' 
                : saving 
                  ? 'bg-gray-600 text-gray-300' 
                  : 'bg-primary text-white active:scale-95'
            } disabled:opacity-50`}
          >
            {saved ? (
              <>
                <Check size={28} />
                Saved!
              </>
            ) : saving ? (
              'Saving...'
            ) : (
              <>
                <Save size={28} />
                Save
              </>
            )}
          </button>

          {/* Toggle Week View */}
          <button
            onClick={() => setShowWeek(true)}
            className="w-full py-3 text-primary border border-primary/30 rounded-xl"
          >
            View Week Summary
          </button>
        </div>
      )}

      {/* Week View */}
      {activeTab === 'punch' && showWeek && weekData && (
        <div className="p-4 space-y-4">
          {/* Week Navigation */}
          <div className="flex items-center justify-between bg-slate-800/50 rounded-xl p-3">
            <button onClick={handlePrevWeek} className="p-2">
              <ChevronLeft size={24} />
            </button>
            <div className="text-center">
              <div className="text-sm text-gray-400">Week</div>
              <div className="font-semibold">
                {new Date(weekData.week_start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - {new Date(weekData.week_end).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
              </div>
            </div>
            <button onClick={handleNextWeek} className="p-2">
              <ChevronRight size={24} />
            </button>
          </div>

          {/* Week Summary */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-slate-800/50 rounded-xl p-4 text-center">
              <div className="text-sm text-gray-400">Total Hours</div>
              <div className="text-2xl font-bold text-primary">
                {formatHoursMinutes(weekData.total_minutes)}
              </div>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-4 text-center">
              <div className="text-sm text-gray-400">Total Amount</div>
              <div className="text-2xl font-bold text-green-400">
                {formatCurrency(weekData.total_amount)}
              </div>
            </div>
          </div>

          {/* Create Invoice Section */}
          {weekData.entries.length > 0 && (
            <div className="bg-slate-800/50 rounded-xl p-4 space-y-3">
              {/* Client Selection */}
              <div>
                <label className="text-sm text-gray-400 block mb-2">Selectează Client</label>
                <select
                  value={selectedClientId}
                  onChange={(e) => setSelectedClientId(e.target.value)}
                  className="w-full bg-slate-900 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-primary"
                >
                  {clients.length === 0 ? (
                    <option value="">Nu există clienți</option>
                  ) : (
                    clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))
                  )}
                </select>
              </div>
              
              {/* Create Invoice Button */}
              <button
                onClick={handleCreateInvoice}
                disabled={creatingInvoice || !selectedClientId}
                className="w-full py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-bold flex items-center justify-center gap-3 disabled:opacity-50 active:scale-[0.98] transition-transform shadow-lg shadow-green-500/20"
              >
                {creatingInvoice ? (
                  'Se creează...'
                ) : (
                  <>
                    <FileText size={20} />
                    Generează Factură
                    <Download size={18} />
                  </>
                )}
              </button>
            </div>
          )}

          {/* Days List */}
          <div className="space-y-2">
            {DAYS_OF_WEEK.map((day, index) => {
              const date = new Date(currentWeekStart);
              date.setDate(date.getDate() + index);
              const dateStr = date.toISOString().split('T')[0];
              const entry = weekData.entries.find(e => e.date === dateStr);
              const isEditing = editingDay?.date === dateStr;
              
              return (
                <div key={day}>
                  {isEditing ? (
                    // Editing mode for this day
                    <div className="bg-primary/20 border-2 border-primary rounded-xl p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-semibold text-primary">{day}</div>
                          <div className="text-xs text-gray-400">
                            {date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                          </div>
                        </div>
                        <button 
                          onClick={() => setEditingDay(null)}
                          className="text-gray-400 hover:text-white p-1"
                        >
                          ✕
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-gray-400 block mb-1">Start</label>
                          <input
                            type="time"
                            value={editingDay.start_time}
                            onChange={(e) => setEditingDay({...editingDay, start_time: e.target.value})}
                            className="w-full bg-slate-900 border border-primary/30 rounded-lg px-3 py-2 text-lg text-center"
                            style={{ colorScheme: 'dark' }}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-400 block mb-1">End</label>
                          <input
                            type="time"
                            value={editingDay.end_time}
                            onChange={(e) => setEditingDay({...editingDay, end_time: e.target.value})}
                            className="w-full bg-slate-900 border border-primary/30 rounded-lg px-3 py-2 text-lg text-center"
                            style={{ colorScheme: 'dark' }}
                          />
                        </div>
                      </div>
                      
                      {/* Quick time buttons */}
                      <div className="flex flex-wrap gap-1 justify-center">
                        {['05:00', '05:30', '06:00', '06:30', '07:00'].map(time => (
                          <button
                            key={`start-${time}`}
                            onClick={() => setEditingDay({...editingDay, start_time: time})}
                            className={`px-2 py-1 rounded text-xs ${editingDay.start_time === time ? 'bg-primary text-white' : 'bg-slate-700'}`}
                          >
                            {time}
                          </button>
                        ))}
                      </div>
                      <div className="flex flex-wrap gap-1 justify-center">
                        {['15:00', '15:30', '16:00', '17:00', '18:00'].map(time => (
                          <button
                            key={`end-${time}`}
                            onClick={() => setEditingDay({...editingDay, end_time: time})}
                            className={`px-2 py-1 rounded text-xs ${editingDay.end_time === time ? 'bg-primary text-white' : 'bg-slate-700'}`}
                          >
                            {time}
                          </button>
                        ))}
                      </div>
                      
                      <button
                        onClick={handleSaveDay}
                        disabled={saving || !editingDay.start_time || !editingDay.end_time}
                        className="w-full py-3 bg-primary text-white rounded-lg font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        {saving ? 'Saving...' : <><Save size={18} /> Save {day}</>}
                      </button>
                    </div>
                  ) : (
                    // Display mode - click to edit
                    <div 
                      onClick={() => handleEditDay(dateStr, day, entry)}
                      className={`flex items-center justify-between p-4 rounded-xl cursor-pointer active:scale-[0.98] transition-transform ${
                        entry ? 'bg-primary/10 border border-primary/30' : 'bg-slate-800/30 border border-transparent'
                      }`}
                    >
                      <div>
                        <div className="font-semibold">{day}</div>
                        <div className="text-xs text-gray-400">
                          {date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {entry ? (
                          <div className="text-right">
                            <div className="text-sm">{entry.start_time} - {entry.end_time}</div>
                            <div className="text-green-400 font-semibold">
                              {formatHoursMinutes(entry.minutes_worked)} = {formatCurrency(entry.amount)}
                            </div>
                          </div>
                        ) : (
                          <div className="text-gray-500 text-sm">Tap to add</div>
                        )}
                        <div className="text-primary/50">✏️</div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Back Button */}
          <button
            onClick={() => setShowWeek(false)}
            className="w-full py-3 text-primary border border-primary/30 rounded-xl"
          >
            ← Back to Today
          </button>
        </div>
      )}
    </div>
  );
};

export default QuickPunchPage;
